import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { EventUtil } from '@monorepo/utils';
import { IApp } from 'libs/types/src/lib/input-app/app-master.types';
import { combineLatest, of } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { AppMasterDBFacade } from '../../redux-store/app-master-db/app-master-db.facade';
import { InputAppImageDBFacade } from '../../redux-store/image-db/image-db.facade';
import { InitStoreFacade } from '../../redux-store/init-db/init-db.facade';
import { InputAppClientDBFacade } from '../../redux-store/input-app-client-db/input-app-client-db.facade';
import {
  BACK_UP_TIME_DIFFERENCE_IN_MILLI_SECONDS,
  DAY_END_CHECK_TIME_DIFFERENCE_IN_MILLI_SECONDS,
  IMAGES_UPLOAD_SYNCING_TIME_DIFFERENCE_IN_MILLI_SECONDS,
  INTERVAL_TO_LOG_PENDING_EVENTS_IN_MILLI_SECONDS,
  INTERVAL_TO_FETCH_LATEST_SESSION_PLANS_IN_MILLI_SECONDS,
  INTERVAL_TO_UPDATE_WORKING_HOURS_STATUS,
  INTERVAL_TO_LOG_DEVICE_STATS_IN_MILLI_SECONDS,
} from '../../redux-store/metadata/metadata.reducer';
import { RoamingQcStoreFacade } from '../../redux-store/roaming-qc-db/roaming-qc-db.facade';
import { ServerDefectivesStoreFacade } from '../../redux-store/server-defectives-db/server-defectives-db.facade';
import { BulkCountAppFacade } from '../../redux-store/bulk-count-app-db/bulk-count-app-db.facade';
import { TBulkWashingEventType } from '@monorepo/types';
import { BundleQcDBFacade } from '../../redux-store/bundle-qc-db/bundle-qc.facade';
import { AqlStoreFacade } from '../../redux-store/aql-db/aql-db.facade';
import { CustomLoggerService } from '@monorepo/services';
import { ShiftsFacade } from '../../redux-store/shifts/shifts.facade';
import { DeviceStatsService } from '../device-stats/device-stats.service';

@Injectable({
  providedIn: 'root',
})
export class AppShellScheduler {
  private hasAlreadyShowingSyncWipPopup = false;
  private lastWipErrorTimestamp: number | undefined;
  private hasStartedDeviceStatsLogging = false;
  constructor(
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private roamingQcStoreFacade: RoamingQcStoreFacade,
    private alertCtrl: AlertController,
    private initStoreFacade: InitStoreFacade,
    private inputAppImageDBFacade: InputAppImageDBFacade,
    private serverDefectivesStoreFacade: ServerDefectivesStoreFacade,
    private appMasterDBFacade: AppMasterDBFacade,
    private bulkCountAppFacade: BulkCountAppFacade,
    private bundleQcFacade: BundleQcDBFacade,
    private aqlDbFacade: AqlStoreFacade,
    private customLoggerService: CustomLoggerService,
    private shiftsFacade: ShiftsFacade,
    private deviceStatsService: DeviceStatsService,
  ) {}

  initBackgroundEventsSyncing(updateActualsOnly: boolean) {
    this.inputAppClientDBFacade
      .getConfigurations()
      .pipe(take(1))
      .subscribe(({ eventsSyncTimeDifferenceInMilliSecondsForAPK }) => {
        setInterval(() => {
          this.inputAppClientDBFacade.backgroundSyncEvents(updateActualsOnly);
        }, eventsSyncTimeDifferenceInMilliSecondsForAPK);
      });
  }

  initIncompleteBundlesSyncing() {
    setInterval(() => {
      this.bundleQcFacade.validateAndSyncIncompleteBundlesForCurrentStation();
    }, 900000);
  }

  initBackgroudWipAndEventsSyncing(updateActualsOnly: boolean) {
    this.initBackgroundEventsSyncing(updateActualsOnly);
    this.inputAppClientDBFacade
      .getConfigurations()
      .pipe(take(1))
      .subscribe(({ bctxSyncTimeDifferenceInMilliSeconds }) => {
        this.appMasterDBFacade
          .getAppIdentifier()
          .pipe(take(1))
          .subscribe((appIdentifier) => {
            setInterval(() => {
              this.inputAppClientDBFacade
                .getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift()
                .pipe(take(1), withLatestFrom(this.inputAppClientDBFacade.getIsPlanBasedBctx()))
                .subscribe(([{ isOnline, userDB, bgDB, isUserDBLocked }, isPlanBasedBctx]) => {
                  if (
                    isOnline &&
                    !isUserDBLocked &&
                    (!EventUtil.hasAnyPendingEvents(userDB, bgDB) || appIdentifier === 'roaming_qc_v2')
                  ) {
                    if (appIdentifier === 'roaming_qc_v2') {
                      // Plan based bctx have no wip to refresh; the plan is fetched on init, line change and sync
                      if (!isPlanBasedBctx) this.inputAppClientDBFacade.syncWipForNonCountingApps(true);
                    } else
                      this.inputAppClientDBFacade.reconcileAndSyncWipForBackground(
                        { discardWipUpdates: updateActualsOnly },
                        updateActualsOnly,
                        false,
                      );
                  } else if (
                    userDB.dailyCache.lastStyleSyncInfoTimestamp &&
                    new Date().getTime() >
                      4 * bctxSyncTimeDifferenceInMilliSeconds + userDB.dailyCache.lastStyleSyncInfoTimestamp &&
                    (!this.lastWipErrorTimestamp ||
                      new Date().getTime() > 4 * bctxSyncTimeDifferenceInMilliSeconds + this.lastWipErrorTimestamp)
                  ) {
                    this.lastWipErrorTimestamp = new Date().getTime();
                    if (!this.hasAlreadyShowingSyncWipPopup) {
                      this.hasAlreadyShowingSyncWipPopup = true;
                      const alert = this.alertCtrl.create({
                        header: 'Warning',
                        subHeader: 'App not synced from a long time',
                        backdropDismiss: false,
                        buttons: [
                          {
                            text: 'Okay',
                            handler: () => {
                              this.hasAlreadyShowingSyncWipPopup = false;
                            },
                          },
                        ],
                      });
                      alert.then((a) => a.present());
                    }
                  }
                });
            }, bctxSyncTimeDifferenceInMilliSeconds);
          });
      });
  }

  getUnlockStatusByAppType(appType: IApp['app_identifier']) {
    switch (appType) {
      case 'md_point_v2':
        return [this.inputAppClientDBFacade.isUserAndBgDBsUnlocked()];
      case 'bundle_qc_v2':
      case 'endline_qc_v2':
      case 'plan_qc_v2':
      case 'per_piece_qc_v2':
      case 'md_point_per_piece_qc_v2':
        return [
          this.inputAppImageDBFacade.isImagesDBsUnlocked(),
          this.serverDefectivesStoreFacade.isServerDefectivesDBUnlocked(),
          this.inputAppClientDBFacade.isUserAndBgDBsUnlocked(),
        ];
      case 'roaming_qc_v2':
        return [
          this.inputAppClientDBFacade.isUserAndBgDBsUnlocked(),
          this.roamingQcStoreFacade.isRoamingQcStoreUnlocked(),
        ];
      case 'per_piece_line_in_v2':
        return [this.inputAppClientDBFacade.isUserAndBgDBsUnlocked()];
      case 'per_piece_washing_in_v2':
      case 'per_piece_washing_out_v2':
        return [this.inputAppClientDBFacade.isUserAndBgDBsUnlocked()];
      case 'aql_v2':
        // ?? TODO NISHA check for userdb too?
        return [this.aqlDbFacade.isAqlDbLocked()];
      case 'bulk_washing_in_v2':
      case 'bulk_washing_out_v2':
        return [this.bulkCountAppFacade.isUserAndBgDBsUnlocked()];
      case 'cpi':
      case 'measurement_qc':
      case 'wip_packer_v2':
        return [of(true)];
      case 'line_in_v2':
        return [this.inputAppClientDBFacade.isUserAndBgDBsUnlocked()];
      default:
        return [];
    }
  }

  initDayEndCheck(appType: IApp['app_identifier']) {
    setInterval(() => {
      this.initStoreFacade
        .getNextCacheResetTimestamp()
        .pipe(
          take(1),
          switchMap((nextCacheResetTimestamp) => {
            return combineLatest(this.getUnlockStatusByAppType(appType)).pipe(
              take(1),
              map((res) => {
                return [nextCacheResetTimestamp, res.every((isUnlocked) => isUnlocked)] as const;
              }),
            );
          }),
        )
        .subscribe(([nextCacheResetTimestamp, isAllDBsUnlocked]) => {
          if (nextCacheResetTimestamp < new Date().getTime() && isAllDBsUnlocked) {
            this.initStoreFacade.acquireLockAndThenUpdateNextCacheResetTimestamp('DAY_END_RESET');
          }
        });
    }, DAY_END_CHECK_TIME_DIFFERENCE_IN_MILLI_SECONDS);
  }

  initBackgroundEventsSyncingForWashing(eventType: TBulkWashingEventType) {
    this.bulkCountAppFacade
      .getConfigurations()
      .pipe(take(1))
      .subscribe(({ eventsSyncTimeDifferenceInMilliSecondsForAPK }) => {
        setInterval(() => {
          this.bulkCountAppFacade.backgroundSyncEvents(eventType);
        }, eventsSyncTimeDifferenceInMilliSecondsForAPK);
      });
  }

  backUpData() {
    this.inputAppClientDBFacade
      .getBackUpDataStatus()
      .pipe(take(1))
      .subscribe((res) => {
        if (res && res.status) {
          setInterval(() => {
            this.inputAppClientDBFacade
              .getClientDB()
              .pipe(take(1))
              .subscribe((res) => {
                if (res.dailyCache.nextCacheResetTimestamp > new Date().getTime()) {
                  this.inputAppClientDBFacade.backUpData();
                }
              });
          }, BACK_UP_TIME_DIFFERENCE_IN_MILLI_SECONDS);
        }
      });
  }

  acquireLockAndSyncImagesInterval() {
    setInterval(() => {
      this.inputAppImageDBFacade.acquireLockAndSyncImages();
    }, IMAGES_UPLOAD_SYNCING_TIME_DIFFERENCE_IN_MILLI_SECONDS);
  }

  /**
   * If config is enabled call this function to initialise, fetch and update the session plans periodically
   * Implemented for apps with appIdentifier: 'endline_qc_v2', 'bundle_qc_v2', 'per_piece_qc_v2'
   */
  fetchLatestSessionPlansAtInterval() {
    this.shiftsFacade.initialiseAndFetchSessionPlans();
    setInterval(() => {
      this.shiftsFacade.fetchSessionPlans(false);
    }, INTERVAL_TO_FETCH_LATEST_SESSION_PLANS_IN_MILLI_SECONDS);
  }

  /**
   * Runs at the start of each minute to evaluate cached session plans
   * and updates a status flag in the store for other components to consume.
   */
  updateWorkingHoursStatus() {
    // Calculate milliseconds until the start of the next minute
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // Set a timeout to run at the start of the next minute and run it at the start of every minute
    // This will ensure that the function runs at the start of every minute for better accuracy
    setTimeout(() => {
      this.shiftsFacade.checkAndUpdateWorkingHoursStatus();

      setInterval(() => {
        this.shiftsFacade.checkAndUpdateWorkingHoursStatus();
      }, INTERVAL_TO_UPDATE_WORKING_HOURS_STATUS);
    }, msUntilNextMinute);
  }

  logPendingEventsPeriodically(appType: IApp['app_identifier'] | false) {
    setInterval(() => {
      this.inputAppClientDBFacade
        .getPendingEventsArray()
        .pipe(take(1))
        .subscribe((pendingEvents) => {
          try {
            if (pendingEvents.length > 0) {
              const appIdentifier = appType ? appType : 'unknown';
              const subjects = Array.from(
                new Set(pendingEvents.map((event) => event.metadata.stationDetails.subject.subject)),
              );
              const hsks = Array.from(new Set(pendingEvents.map((event) => event.metadata.stationDetails.subject.hsk)));
              const subjectsList = subjects.join(',');
              const hsksList = hsks.join(',');
              const logMessage = `subject: ${subjectsList} | hsk: ${hsksList} | App Identifier: ${appIdentifier} | Pending events detected `;
              this.customLoggerService.log(logMessage, pendingEvents);
            }
          } catch (error) {
            console.error('Error logging pending events:', error);
          }
        });
    }, INTERVAL_TO_LOG_PENDING_EVENTS_IN_MILLI_SECONDS);
  }

  /**
   * Logs a RAM + CPU snapshot every 5 minutes on native builds, for every app
   *
   * Safe to call more than once: only the first call starts the interval.
   */
  logDeviceStatsPeriodically() {
    // No timer at all on the web build - there is no native plugin there to read from.
    if (!this.deviceStatsService.isSupported()) return;
    if (this.hasStartedDeviceStatsLogging) return;
    this.hasStartedDeviceStatsLogging = true;

    // The very first snapshot has no CPU usage percentage, as that is a delta between two readings.
    this.logDeviceStats();
    setInterval(() => this.logDeviceStats(), INTERVAL_TO_LOG_DEVICE_STATS_IN_MILLI_SECONDS);
  }

  private async logDeviceStats() {
    try {
      const snapshot = await this.deviceStatsService.getStatsSnapshot();
      this.customLoggerService.log(`[DEVICE_STATS] ${this.deviceStatsService.describeSnapshot(snapshot)}`, snapshot);
    } catch (error) {
      console.error('Error logging device stats:', error);
    }
  }
}
